import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '../../components/ui/FormField';
import NumericInput from '../../components/NumericInput';
import ToggleSwitch from '../../components/forms/ToggleSwitch';
import SearchableSelect from '../../components/SearchableSelect';
import type { SchemaFieldProps } from '../types';

type IntervalKey = 'yearly' | 'half_year' | 'quarter' | 'monthly';

/**
 * Persisted calendar config jsonb shape (`memberships.calendarConfig`). Only the
 * enabled intervals with a positive price are kept — the MembershipForm wrapper
 * derives this from the editing state below on submit.
 */
export interface CalendarConfig {
  startMonth: number;
  intervals: Partial<Record<IntervalKey, number>>;
}

/**
 * Rich editing state carried by the `calendarConfig` field. Each interval keeps
 * its own enabled flag + price so toggling off doesn't lose the typed amount,
 * matching the former MembershipForm behaviour. The wrapper collapses this into
 * the persisted CalendarConfig (enabled && > 0 only) at submit time.
 */
export interface CalendarConfigDraft {
  startMonth: number;
  yearly: number;
  halfYear: number;
  quarter: number;
  monthly: number;
  yearlyEnabled: boolean;
  halfYearEnabled: boolean;
  quarterEnabled: boolean;
  monthlyEnabled: boolean;
}

export const emptyCalendarConfigDraft = (): CalendarConfigDraft => ({
  startMonth: 1,
  yearly: 0,
  halfYear: 0,
  quarter: 0,
  monthly: 0,
  yearlyEnabled: true,
  halfYearEnabled: false,
  quarterEnabled: false,
  monthlyEnabled: false,
});

/** Build the editing draft from a persisted CalendarConfig (edit flow). */
export const draftFromCalendarConfig = (cc?: CalendarConfig | null): CalendarConfigDraft => {
  const draft = emptyCalendarConfigDraft();
  if (!cc) return draft;
  draft.startMonth = cc.startMonth || 1;
  if (cc.intervals.yearly) { draft.yearlyEnabled = true; draft.yearly = cc.intervals.yearly; }
  if (cc.intervals.half_year) { draft.halfYearEnabled = true; draft.halfYear = cc.intervals.half_year; }
  if (cc.intervals.quarter) { draft.quarterEnabled = true; draft.quarter = cc.intervals.quarter; }
  if (cc.intervals.monthly) { draft.monthlyEnabled = true; draft.monthly = cc.intervals.monthly; }
  return draft;
};

/** Collapse the editing draft into the persisted CalendarConfig payload. */
export const calendarConfigFromDraft = (draft: CalendarConfigDraft): CalendarConfig => {
  const intervals: CalendarConfig['intervals'] = {};
  if (draft.yearlyEnabled && draft.yearly > 0) intervals.yearly = draft.yearly;
  if (draft.halfYearEnabled && draft.halfYear > 0) intervals.half_year = draft.halfYear;
  if (draft.quarterEnabled && draft.quarter > 0) intervals.quarter = draft.quarter;
  if (draft.monthlyEnabled && draft.monthly > 0) intervals.monthly = draft.monthly;
  return { startMonth: draft.startMonth, intervals };
};

/**
 * Custom SchemaForm widget for the membership `calendarConfig` jsonb field
 * (calendar type). Extracted verbatim (behaviour-wise) from MembershipForm's
 * former inline start-month select + interval toggle/price rows. Rebuilt with
 * FormField + SearchableSelect + ToggleSwitch + NumericInput (no raw
 * select/inputs).
 */
const CalendarConfigField: React.FC<SchemaFieldProps> = ({ value, onChange }) => {
  const { t } = useTranslation();

  const draft: CalendarConfigDraft = value ?? emptyCalendarConfigDraft();

  const patch = useCallback(
    (next: Partial<CalendarConfigDraft>) => {
      onChange({ ...draft, ...next });
    },
    [onChange, draft]
  );

  const monthOptions = useMemo(
    () => [
      { _id: 1, name: t('memberships.monthJan') },
      { _id: 2, name: t('memberships.monthFeb') },
      { _id: 3, name: t('memberships.monthMar') },
      { _id: 4, name: t('memberships.monthApr') },
      { _id: 5, name: t('memberships.monthMay') },
      { _id: 6, name: t('memberships.monthJun') },
      { _id: 7, name: t('memberships.monthJul') },
      { _id: 8, name: t('memberships.monthAug') },
      { _id: 9, name: t('memberships.monthSep') },
      { _id: 10, name: t('memberships.monthOct') },
      { _id: 11, name: t('memberships.monthNov') },
      { _id: 12, name: t('memberships.monthDec') },
    ],
    [t]
  );

  const intervalRows: Array<{
    enabled: boolean;
    enabledKey: keyof CalendarConfigDraft;
    value: number;
    valueKey: keyof CalendarConfigDraft;
    label: string;
  }> = [
    { enabled: draft.yearlyEnabled, enabledKey: 'yearlyEnabled', value: draft.yearly, valueKey: 'yearly', label: t('memberships.intervalYearly') },
    { enabled: draft.halfYearEnabled, enabledKey: 'halfYearEnabled', value: draft.halfYear, valueKey: 'halfYear', label: t('memberships.intervalHalfYear') },
    { enabled: draft.quarterEnabled, enabledKey: 'quarterEnabled', value: draft.quarter, valueKey: 'quarter', label: t('memberships.intervalQuarter') },
    { enabled: draft.monthlyEnabled, enabledKey: 'monthlyEnabled', value: draft.monthly, valueKey: 'monthly', label: t('memberships.intervalMonthly') },
  ];

  return (
    <>
      <FormField htmlFor="sf-startMonth" label={t('memberships.startMonth')}>
        <SearchableSelect
          id="sf-startMonth"
          options={monthOptions}
          value={draft.startMonth}
          allowClear={false}
          onChange={(v) => patch({ startMonth: v == null ? 1 : Number(v) })}
        />
      </FormField>

      <FormField htmlFor="sf-calendarIntervals" label={t('memberships.calendarIntervals')}>
        <div id="sf-calendarIntervals" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {intervalRows.map((row) => (
            <div key={row.valueKey} className="interval-row">
              <ToggleSwitch
                checked={row.enabled}
                onChange={(checked) => patch({ [row.enabledKey]: checked } as Partial<CalendarConfigDraft>)}
              />
              <span className="interval-row__name">{row.label}</span>
              <NumericInput
                min="0"
                step="1"
                value={row.value === 0 ? '' : row.value}
                onChange={(e) =>
                  patch({ [row.valueKey]: e.target.value === '' ? 0 : parseFloat(e.target.value) } as Partial<CalendarConfigDraft>)
                }
                disabled={!row.enabled}
                placeholder={t('memberships.intervalPrice')}
                className="interval-row__price"
              />
              <span className="interval-row__currency">CZK</span>
            </div>
          ))}
        </div>
      </FormField>
    </>
  );
};

export default CalendarConfigField;
